import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Avatar, Divider, Chip, Grid, Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useDealer, useCertificationToolDealer } from 'services/Data/DealerServices';
import { useProductLines } from 'services/Data/ProductLineServices';
import {
  getActiveProductOfferings,
  getProductLineColor,
  getProductLineIcon,
} from 'services/Utilities/productOfferingsConfig';
import { translateLabel } from 'services/Utilities/labelUtils';

const AccountPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const basePaperSx = useMemo(
    () => ({
      bgcolor: isDarkMode ? '#1a1a1a' : theme.palette.background.paper,
      borderRadius: 2,
      boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.8)' : '0 18px 28px rgba(15, 23, 42, 0.12)',
      border: isDarkMode ? '1px solid rgba(255, 210, 0, 0.1)' : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    }),
    [isDarkMode, theme],
  );
  const account = useSelector(state => state.AccountReducer);
  const { data: productLines = [] } = useProductLines();
  const { data: dealer } = useDealer();
  const dealerId = account?.profile?.dealer_Id || dealer?.id;
  const { data: certificationDealer } = useCertificationToolDealer(dealerId, { enabled: !!dealerId });
  const dealerInfo = certificationDealer || dealer;
  const dealerProductLines = useMemo(() => {
    return certificationDealer?.dealerProductLines ?? dealer?.dealerProductLines ?? [];
  }, [certificationDealer, dealer]);
  const dealerForOfferings = useMemo(() => {
    if (!dealerInfo && dealerProductLines.length === 0) {
      return null;
    }

    return {
      ...(dealerInfo || {}),
      dealerProductLines,
    };
  }, [dealerInfo, dealerProductLines]);

  const user = useMemo(() => {
    if (!account) {
      return null;
    }

    const person = account.profile?.person || {};

    return {
      firstName: person.firstName || account.firstName,
      lastName: person.lastName || account.lastName,
      email: person.email || account.username,
      username: account.username,
    };
  }, [account]);

  const getInitials = () => {
    if (!user) return '??';
    const firstInitial = user.firstName?.charAt(0) || '';
    const lastInitial = user.lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const activeProducts = useMemo(() => {
    return getActiveProductOfferings(dealerForOfferings, productLines);
  }, [dealerForOfferings, productLines]);

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', lg: 1400, xl: 1600 },
        mx: 'auto',
        px: { xs: 0, sm: 1.5 },
        my: 12,
      }}>
      {/* Header Section */}
      <Paper
        elevation={8}
        sx={{
          ...basePaperSx,
          p: { xs: 3, md: 4 },
          mb: 3,
        }}>
        <Typography
          variant="h2"
          sx={{
            color: '#FFD200',
            fontWeight: 900,
            mb: 3,
            textTransform: 'uppercase',
          }}>
          {t('certAccount.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{
              bgcolor: '#FFD200',
              color: '#000',
              fontWeight: 700,
              width: 80,
              height: 80,
              fontSize: 32,
              mr: 3,
            }}>
            {getInitials()}
          </Avatar>
          <Box>
            <Typography variant="h2" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary, fontWeight: 700 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.primary, 0.7) }}>
              {user?.email || user?.username}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Dealer Information Grid */}

      <Grid container spacing={3} alignItems="stretch">
        {/* Main Dealer Info Card */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={8}
            sx={{
              ...basePaperSx,
              p: { xs: 3, md: 3.5 },
              height: '100%',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <StoreIcon sx={{ fontSize: 32, color: '#FFD200', mr: 2 }} />
              <Typography variant="h2" sx={{ color: '#FFD200', fontWeight: 900, textTransform: 'uppercase' }}>
                {t('certAccount.dealerInformation')}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h2" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary, fontWeight: 700 }}>
                {dealerInfo?.customerName || 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.primary, 0.7), mb: 1 }}>
                {dealerInfo?.customerLegalName}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5, alignItems: 'flex-start' }}>
                <Chip
                  label={`ID: ${dealerInfo?.identifier || 'N/A'}`}
                  size="small"
                  sx={{
                    bgcolor: '#FFD200',
                    color: '#000',
                    fontWeight: 700,
                  }}
                />
                {dealerInfo && (
                  <Chip
                    label={dealerInfo.isBrpExclusive ? t('submission.brponly') : t('submission.multioem')}
                    size="small"
                    sx={{
                      bgcolor: dealerInfo.isBrpExclusive ? theme.palette.primary.main : theme.palette.info.main,
                      color: dealerInfo.isBrpExclusive ? theme.palette.common.black : theme.palette.common.white,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Divider
              sx={{
                my: 3,
                borderColor: isDarkMode ? 'rgba(255, 210, 0, 0.1)' : alpha(theme.palette.common.black, 0.08),
              }}
            />

            {/* Contact Information */}
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {dealerInfo?.principalContact && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <StoreIcon sx={{ color: '#FFD200', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                      {t('certAccount.principalContact')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary, fontWeight: 600 }}>
                      {dealerInfo.principalContact}
                    </Typography>
                  </Box>
                </Box>
              )}

              {(dealerInfo?.civicNumber || dealerInfo?.city) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationIcon sx={{ color: '#FFD200', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                      {t('certAccount.address')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary }}>
                      {dealerInfo.civicNumber}
                    </Typography>
                    {dealerInfo.poBox && (
                      <Typography variant="body1" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary }}>
                        {dealerInfo.poBox}
                      </Typography>
                    )}
                    <Typography variant="body1" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary }}>
                      {dealerInfo.city}, {dealerInfo.postalCode}
                    </Typography>
                  </Box>
                </Box>
              )}

              {dealerInfo?.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <PhoneIcon sx={{ color: '#FFD200', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                      {t('certAccount.phone')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary }}>
                      {dealerInfo.phoneNumber}
                    </Typography>
                    {dealerInfo.fax && (
                      <Typography
                        variant="body2"
                        sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.primary, 0.7) }}>
                        {t('certAccount.fax')}: {dealerInfo.fax}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {dealerInfo?.email && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <EmailIcon sx={{ color: '#FFD200', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                      {t('certAccount.email')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary }}>
                      {dealerInfo.email}
                    </Typography>
                  </Box>
                </Box>
              )}

              {dealerInfo?.website && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <WebsiteIcon sx={{ color: '#FFD200', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                      {t('certAccount.website')}
                    </Typography>
                    <Typography
                      variant="body1"
                      component="a"
                      href={dealerInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#FFD200',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}>
                      {dealerInfo.website.replace(/^https?:\/\//i, '')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Product Offerings Card */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={8}
            sx={{
              ...basePaperSx,
              p: { xs: 3, md: 3.5 },
              height: '100%',
            }}>
            <Typography
              variant="h2"
              sx={{
                color: '#FFD200',
                fontWeight: 900,
                mb: 3,
                textTransform: 'uppercase',
              }}>
              {t('certAccount.productOfferings')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activeProducts.length > 0 ? (
                activeProducts.map(productLine => {
                  const label =
                    translateLabel(productLine.name, i18n.language) ||
                    productLine.internalName ||
                    productLine.code ||
                    productLine.id;

                  const color = getProductLineColor(productLine.internalName);
                  const IconComponent = getProductLineIcon(productLine.internalName);
                  return (
                    <Chip
                      key={productLine.id}
                      icon={<IconComponent />}
                      label={label}
                      sx={{
                        bgcolor: color,
                        color: '#fff',
                        fontWeight: 600,
                        py: 2.5,
                        px: 1,
                        '& .MuiChip-icon': {
                          color: '#fff',
                        },
                        justifyContent: 'flex-start',
                      }}
                    />
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : alpha(theme.palette.text.primary, 0.7) }}>
                  {t('certAccount.noProductOfferings')}
                </Typography>
              )}
            </Box>

            {dealerInfo?.offersBOSSWeb && (
              <Box sx={{ mt: 3 }}>
                <Chip
                  label={t('certAccount.bossWebEnabled')}
                  size="small"
                  sx={{
                    bgcolor: '#4CAF50',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}

            <Divider
              sx={{
                my: 3,
                borderColor: isDarkMode ? 'rgba(255, 210, 0, 0.1)' : alpha(theme.palette.common.black, 0.08),
              }}
            />

            <Box>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                {t('certAccount.status')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={dealerInfo?.isActive ? t('certAccount.active') : t('certAccount.inactive')}
                  size="small"
                  sx={{
                    bgcolor: dealerInfo?.isActive ? '#4CAF50' : '#F44336',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            {dealerInfo?.dateCreated && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : alpha(theme.palette.text.primary, 0.6) }}>
                  {t('certAccount.memberSince')}
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : theme.palette.text.primary, mt: 0.5 }}>
                  {new Date(dealerInfo.dateCreated).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                  })}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountPage;
